import {
  Card,
  Flex,
  Popover,
  SimpleTable,
  Typography,
  Button,
  Input,
} from "djuno-design";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const PopoverPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Popover</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Popover component in Djuno Design allows for customizable
            popovers with flexible content and styling options. It provides a
            way to display additional information or actions in a floating panel
            that appears when interacting with a trigger element.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Popover component offers the following customization options:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Content:
                </Text>{" "}
                Specifies the element or content that triggers the popover when
                clicked.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Children:
                </Text>{" "}
                Optional content that can be rendered inside the popover.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Panel Class Name:
                </Text>{" "}
                Apply additional CSS classes to the popover panel for custom
                styling.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Panel Style:
                </Text>{" "}
                Additional inline styles that can be applied to the popover
                panel.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Anchor Positioning
                </Text>
                : You can control the dropdown’s anchor positioning with the{" "}
                <Text code>anchor</Text> prop. This allows flexibility in how
                the dropdown is aligned and displayed relative to its trigger
                element. Supported anchor positions include combinations of
                horizontal and vertical alignment such as "bottom start", "top
                end", etc.
              </Text>
            </li>
          </ul>

          {/* Examples */}

          <Flex direction="col">
            <Title level={5} className="mt-5">
              Examples
            </Title>

            <Flex direction="col" className="gap-4 ">
              <Flex direction={{ default: "col", lg: "row" }} className="gap-4">
                <ExpandableCard
                  description="Popover"
                  codeViewerProps={{
                    code: PaopoverExample1,
                  }}
                >
                  <Flex className="mb-10 ">
                    {" "}
                    <Popover
                      content={
                        <Input
                          value="djuno-design"
                          containerClassName="w-[102px] "
                        />
                      }
                      panelClassName="z-1000 min-w-600 max-w-600 whitespace-nowrap "
                      panelStyle={{}}
                    >
                      <Button>Popover</Button>
                    </Popover>
                  </Flex>
                </ExpandableCard>
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* content */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">content</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The content or element that triggers the popover when
                        clicked.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Optional children to be rendered inside the popover.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* panelClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">panelClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Additional CSS classes to apply to the popover panel.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* panelStyle */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">panelStyle</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Additional inline styles to apply to the popover panel.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        React.CSSProperties
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* anchor */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">anchor</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        the properties related to the anchor positioning or
                        alignment of UI component
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "IDropdown.ts#L67"}
                        size="xs"
                        code
                      >
                        AnchorProps
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const PaopoverExample1 = `<Popover
  content={<Input value="djuno-design" />}
  panelClassName="z-1000 min-w-600 max-w-600 whitespace-nowrap"
  panelStyle={{}}
>
  <Button>Popover</Button>
</Popover>`;

export default PopoverPage;
